import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom'; // 1. Import ReactDOM for portals
import './MultiSelectDropdown.css'; // Import styles

// Move MultiSelectDropdown to its own file, e.g. src/components/common/MultiSelectDropdown.js
// --- MultiSelectDropdown.js ---
export const MultiSelectDropdown = ({ options, selectedValues = [], onChange, placeholder, disabled, id, displayKey, valueKey }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null); // Ref for the main component
    const optionsRef = useRef(null); // Ref for the floating options panel

    // Helper to get value from option
    const getValue = (option) => {
        if (valueKey && typeof option === 'object') return option[valueKey];
        return typeof option === 'string' ? option.toUpperCase() : option;
    };

    // Helper to get label from option
    const getLabel = (option) => {
        if (displayKey && typeof option === 'object') return option[displayKey];
        return typeof option === 'string' ? option.toUpperCase() : String(option);
    };

    // 2. Function to position the floating panel
    const positionOptions = useCallback(() => {
        if (isOpen && dropdownRef.current && optionsRef.current) {
            // The component may live in a window.open popout — measure against
            // the document/window that actually owns the element.
            const doc = dropdownRef.current.ownerDocument || document;
            const view = doc.defaultView || window;
            const rect = dropdownRef.current.getBoundingClientRect();
            const optionsEl = optionsRef.current;
            optionsEl.style.position = 'absolute';
            optionsEl.style.top = `${rect.bottom + view.scrollY}px`;
            optionsEl.style.left = `${rect.left + view.scrollX}px`;
            // Fix width to match the dropdown exactly
            optionsEl.style.width = `${rect.width}px`;
        }
    }, [isOpen]);

    // 3. Effect to handle positioning and outside clicks
    useEffect(() => {
        if (!isOpen) return;

        positionOptions(); // Position on open

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                optionsRef.current && !optionsRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        // Listen on the document that owns the element (popout-aware)
        const doc = (dropdownRef.current && dropdownRef.current.ownerDocument) || document;
        const view = doc.defaultView || window;
        doc.addEventListener('mousedown', handleClickOutside);
        view.addEventListener('scroll', positionOptions, true); // Use capture phase for scroll
        view.addEventListener('resize', positionOptions);

        return () => {
            // Cleanup listeners
            doc.removeEventListener('mousedown', handleClickOutside);
            view.removeEventListener('scroll', positionOptions, true);
            view.removeEventListener('resize', positionOptions);
        };
    }, [isOpen, positionOptions]);


    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleOptionChange = (option, checked) => {
        const currentValues = Array.isArray(selectedValues) ? selectedValues : [];
        const val = getValue(option);
        const newValues = checked
            ? [...currentValues, val]
            // Use loose inequality used to handle type mismatches
            : currentValues.filter(v => v != val);
        onChange(newValues);
    };

    const displayText = () => {
        const values = Array.isArray(selectedValues) ? selectedValues : [];
        if (values.length === 0) return placeholder;

        if (valueKey && options.length > 0) {
            return values.map(v => {
                // Use loose equality to handle type mismatches (string vs number)
                const opt = options.find(o => getValue(o) == v);
                return opt ? getLabel(opt) : v;
            }).join(', ');
        }

        return values.map(v => String(v).toUpperCase()).join(', ');
    };

    // 4. Define the JSX for the options panel
    const optionsPanel = (
        <div className="multiselect-options open" ref={optionsRef} style={{ zIndex: 100010 }}>
            {options.map(option => {
                const val = getValue(option);
                const label = getLabel(option);
                // Use loose equality for inclusion check
                const isChecked = Array.isArray(selectedValues) && selectedValues.some(v => v == val);
                
                return (
                    <div key={val} className="multiselect-option">
                        <input
                            type="checkbox"
                            id={`${id}-${val}`}
                            checked={isChecked}
                            onChange={(e) => handleOptionChange(option, e.target.checked)}
                        />
                        <label htmlFor={`${id}-${val}`}>{label}</label>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="multiselect-dropdown" ref={dropdownRef}>
            <div 
                className={`multiselect-header ${disabled ? 'disabled' : ''}`}
                onClick={handleToggle}
                title={displayText()}
            >
                <span className="multiselect-selected-items">{displayText()}</span>
                <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
            </div>
            {/* 5. Render the options panel inside a portal — into the document
                   that owns this element so it also works inside popout windows */}
            {isOpen && !disabled && ReactDOM.createPortal(
                optionsPanel,
                (dropdownRef.current && dropdownRef.current.ownerDocument && dropdownRef.current.ownerDocument.body) || document.body
            )}

        </div>
    );
};
